<template>
	<div :class="[{ 'schedule-filters': $vuetify.display.width > 1500 }, 'mt-3 flex-wrap']">
		<div style="flex: 1; min-width: 250px" v-if="$vuetify.display.width > 1500">
			<ButtonGroup :items="tabs" v-model="state.tab"></ButtonGroup>
		</div>

		<!-- <div style="flex: 1"></div> -->
		<ConferenceDaysNav
			style="flex: 1; max-width: 540px; margin: auto"
			v-model:active-day="state.day"
			:days="event.conference_days"
		></ConferenceDaysNav>
		<div style="flex: 1" v-if="$vuetify.display.width > 1500">
			<v-text-field
				v-if="state.day"
				clearable
				v-model="state.search"
				label="Search"
				class="custom-input"
				style="max-width: 350px; margin-left: auto; flex: 1"
				density="comfortable"
				color="primary"
				bg-color="white"
			></v-text-field>
		</div>
		<template v-else>
			<div class="d-flex flex-wrap gap-3 mt-3">
				<div style="flex-grow: 1">
					<ButtonGroup
						data-cy="schedule-tab-group"
						style="margin: auto"
						:items="tabs"
						v-model="state.tab"
					></ButtonGroup>
				</div>
				<div style="align-self: center; flex-grow: 2">
					<v-text-field
						v-if="state.day"
						clearable
						v-model="state.search"
						label="Search"
						class="custom-input"
						style="max-width: 350px; min-width: 250px; margin-left: auto"
						density="comfortable"
						color="primary"
						bg-color="white"
					></v-text-field>
				</div>
			</div>
		</template>
	</div>
	<div class="table-con" v-if="state.day">
		<SchedulesTable
			:schedule-type="state.tab == 'teachers' ? 'teacher' : 'team'"
			:event-day="conferenceDay"
			:search="state.search || ''"
			:loading-event="loadingEventDay"
			@add:schedule="state.tab == 'teachers' ? conferenceDay.conference_teachers.push($event as any) : conferenceDay.conference_teams.push($event as any)"
			@delete:schedule="
				state.tab == 'teachers'
					? (conferenceDay.conference_teachers = conferenceDay.conference_teachers.filter(
							(t) => t.teacher_uid !== $event
					  ))
					: (conferenceDay.conference_teams = conferenceDay.conference_teams.filter(
							(t) => t.team_uid !== $event
					  ))
			"
			@add:breaks="state.addBreaksModal = true"
			@remove:breaks="state.removeBreaksModal = true"
		></SchedulesTable>
	</div>
	<div
		v-else
		style="
			height: calc(100% - 60px);
			background-color: rgba(246, 247, 237, 0.5);
			border-radius: 15px;
		"
	>
		<div class="empty-state" style="background: none">
			<figure style="max-width: 300px">
				<IconListEmptyState />
			</figure>
			<div class="title">There are no event days for this conference</div>
			<p>Please go to edit event page to add event day</p>
			<RouterLink
				v-if="authStore.hasPolicy('school.events.general.write')"
				:to="{
					name: 'school-edit-events',
					params: {
						eventId: eventId,
					},
				}"
				data-cy="schedule-body-goto-edit-btn"
			>
				<v-btn append-icon="mdi-calendar-edit">edit</v-btn>
			</RouterLink>
		</div>
	</div>
	<AddBreaksModal
		v-model:show="state.addBreaksModal"
		:day="conferenceDay"
		:tab="state.tab"
	></AddBreaksModal>
	<RemoveBreaksModal
		v-model:show="state.removeBreaksModal"
		:day="conferenceDay"
		:tab="state.tab"
	></RemoveBreaksModal>
</template>

<script setup lang="ts">
import type { DayWithNested } from "@/api_types/school";
import ButtonGroup from "@/components/ButtonGroup.vue";
import ConferenceDaysNav from "@/components/ConferenceDaysNav.vue";
import FetchService from "@/services/fetch.service";
import { useConferenceStore } from "@/stores/teacher/conference";
import { asyncComputed } from "@/utils/asyncComputed";
import { watch } from "vue";
import { reactive } from "vue";
import { computed } from "vue";
import SchedulesTable from "./SchedulesTable.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import AddBreaksModal from "@/components/school/schedule/BreaksModal.vue";
import RemoveBreaksModal from "./removeBreaksModal.vue";
import { useAuthenticationStore } from "@/stores/authentication";

defineProps<{
	eventId: string;
}>();

const confStore = useConferenceStore();
const authStore = useAuthenticationStore();

const event = computed(() => confStore.selected!);

const state = reactive({
	tab: "teachers" as "teachers" | "teams",
	day: "",
	search: "",
	addBreaksModal: false,
	removeBreaksModal: false,
});

if (event.value) state.day = event.value.conference_days[0]?.conference_days_id || "";
watch(event, () => {
	state.day = event.value.conference_days[0]?.conference_days_id || "";
});

const tabs = [
	{
		title: "Teachers",
		value: "teachers",
	},
	{
		title: "Teams",
		value: "teams",
	},
];

const {
	data: conferenceDay,
	reload: reloadDay,
	loading: loadingEventDay,
} = asyncComputed(
	async (controller) => {
		if (!state.day) return {} as DayWithNested;
		const res = (await FetchService.fetchData(
			`/conference-days/${state.day}`,
			"get",
			undefined,
			undefined,
			controller
		)) as DayWithNested;
		if (res?.conference_days_id) return res;
		return {} as DayWithNested;
	},
	[() => state.day],
	undefined,
	undefined,
	{}
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.schedule-filters {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.day-btn {
	width: 100%;
	padding: 15px;
	display: flex;
	align-items: center;
	justify-content: center;

	@include breakpoints.media-breakpoint-down(md) {
		padding: 0;
		display: block;
	}

	&.smaller {
		padding: 0;
		display: block;
		small {
			display: block;
		}
	}
	small {
		vertical-align: middle;
		font-weight: 500;
		font-size: 0.75rem;
		text-transform: capitalize;
		@include breakpoints.media-breakpoint-down(md) {
			display: block;
		}
	}
	span {
		font-size: 1.25rem;
		line-height: 1;
		font-weight: 600;
		padding: 0.25rem 0.5rem;
	}
}
</style>
